import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { Loader2, Gift, Calculator, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const Field = ({ label, ...p }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
    <input {...p} className="mt-1 w-full border border-black/15 px-2 py-1.5 text-sm" />
  </label>
);
const Banner = ({ children }) => (
  <div data-testid="promo-disabled-banner" className="bg-taven-gray/50 border border-black/10 p-4 text-sm mb-6">
    {children}
  </div>
);

export default function AdminPromotionStudio() {
  const client = adminClient();
  const [enabled, setEnabled] = useState(null);
  const [promos, setPromos] = useState([]);
  const [tasteIn, setTasteIn] = useState({ supplier: 40, packaging: 15, handling: 10, shipping: 120, platform: 20, refund_reserve: 10, fraud_reserve: 5, taxes: 30 });
  const [tasteOut, setTasteOut] = useState(null);
  const [bogoIn, setBogoIn] = useState({ sale: 2000, item1: 300, item2: 300, shipping: 250, handling: 40, payment_fee: 60, refund_reserve: 30, chargeback_reserve: 20 });
  const [bogoOut, setBogoOut] = useState(null);
  const [name, setName] = useState("");
  const [ptype, setPtype] = useState("taste_it");

  const load = () => {
    client.get("/admin/promotions/engine").then(({ data }) => { setEnabled(true); setPromos(data || []); })
      .catch((e) => { if (e?.response?.status === 503) setEnabled(false); else setEnabled(false); });
  };
  useEffect(load, []);

  const previewTaste = () => {
    const costs = { supplier: +tasteIn.supplier, packaging: +tasteIn.packaging, handling: +tasteIn.handling, shipping: +tasteIn.shipping, platform: +tasteIn.platform, refund_reserve: +tasteIn.refund_reserve, fraud_reserve: +tasteIn.fraud_reserve };
    client.post("/admin/promotions/engine/preview/taste-it", { costs, taxes_cents: +tasteIn.taxes })
      .then(({ data }) => setTasteOut(data)).catch((e) => toast.error(e?.response?.status === 503 ? "Engine disabled" : "Preview failed"));
  };
  const previewBogo = () => {
    const costs = { item1: +bogoIn.item1, item2: +bogoIn.item2, shipping: +bogoIn.shipping, handling: +bogoIn.handling, payment_fee: +bogoIn.payment_fee, refund_reserve: +bogoIn.refund_reserve, chargeback_reserve: +bogoIn.chargeback_reserve };
    client.post("/admin/promotions/engine/preview/bogo", { sale_price_cents: +bogoIn.sale, costs })
      .then(({ data }) => setBogoOut(data)).catch((e) => toast.error(e?.response?.status === 503 ? "Engine disabled" : "Preview failed"));
  };
  const create = () => {
    if (!name) return toast.error("Name required");
    client.post("/admin/promotions/engine", { type: ptype, name }).then(() => { toast.success("Promotion created (draft)"); setName(""); load(); })
      .catch((e) => toast.error(e?.response?.status === 503 ? "Engine disabled" : "Create failed"));
  };

  return (
    <div data-testid="admin-promotion-studio">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-black font-display flex items-center gap-2"><Gift className="w-5 h-5" /> Promotion Studio</h2>
        <button onClick={load} data-testid="promo-refresh" className="flex items-center gap-1.5 text-xs font-bold border border-black/15 px-3 py-2 hover:bg-taven-gray"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      {enabled === false && (
        <Banner>The Promotion Engine is <b>disabled (fail-closed)</b>. Pricing previews and creation are locked until <code>PROMOTION_ENGINE_ENABLED=true</code> and a release gate is approved. This is expected in production until authorization.</Banner>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Taste It calculator */}
        <div className="border border-black/10 p-4">
          <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-1.5"><Calculator className="w-4 h-4" /> Taste It — Transparent Pricing</h3>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {["supplier", "packaging", "handling", "shipping", "platform", "refund_reserve", "fraud_reserve", "taxes"].map((k) => (
              <Field key={k} label={`${k} (¢)`} type="number" data-testid={`taste-${k}`} value={tasteIn[k]} onChange={(e) => setTasteIn({ ...tasteIn, [k]: e.target.value })} />
            ))}
          </div>
          <button onClick={previewTaste} data-testid="taste-preview-btn" className="w-full bg-taven-orange text-white font-bold py-2 text-sm hover:opacity-90">Preview Total</button>
          {tasteOut && (
            <div data-testid="taste-result" className="mt-3 text-sm bg-white border border-black/10 p-3">
              <p>Product price: <b>{money(tasteOut.product_price_cents)}</b></p>
              <p>Fulfillment &amp; delivery fee: <b>{money(tasteOut.fulfillment_fee_cents)}</b></p>
              <p>Taxes: {money(tasteOut.taxes_cents)}</p>
              <p className="text-base">Complete total: <b>{money(tasteOut.total_cents)}</b></p>
              <p>Net profit: <b className={tasteOut.ok ? "text-green-700" : "text-red-600"}>{money(tasteOut.net_profit_cents)}</b> {tasteOut.ok ? "✓ above floor" : `✗ ${tasteOut.blocks.join(", ")}`}</p>
              <p className="text-[11px] text-muted-foreground mt-1">{tasteOut.disclosure}</p>
            </div>
          )}
        </div>

        {/* BOGO calculator */}
        <div className="border border-black/10 p-4">
          <h3 className="text-sm font-black uppercase tracking-wider mb-3 flex items-center gap-1.5"><Calculator className="w-4 h-4" /> BOGO Express — Balance Check</h3>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {["sale", "item1", "item2", "shipping", "handling", "payment_fee", "refund_reserve", "chargeback_reserve"].map((k) => (
              <Field key={k} label={`${k} (¢)`} type="number" data-testid={`bogo-${k}`} value={bogoIn[k]} onChange={(e) => setBogoIn({ ...bogoIn, [k]: e.target.value })} />
            ))}
          </div>
          <button onClick={previewBogo} data-testid="bogo-preview-btn" className="w-full bg-taven-orange text-white font-bold py-2 text-sm hover:opacity-90">Check Balance</button>
          {bogoOut && (
            <div data-testid="bogo-result" className="mt-3 text-sm bg-white border border-black/10 p-3">
              <p>Required to cover: <b>{money(bogoOut.required_cents)}</b> · Sale: <b>{money(bogoOut.sale_price_cents)}</b></p>
              <p>Seller benefit: {money(bogoOut.seller_benefit_cents)} · TAVEN net: {money(bogoOut.taven_net_cents)} · Platform: {money(bogoOut.platform_allocation_cents)} · Affiliate: {money(bogoOut.affiliate_cents)}</p>
              <p>Surplus: <b className={bogoOut.ok ? "text-green-700" : "text-red-600"}>{money(bogoOut.surplus_cents)}</b> — {bogoOut.ok ? "✓ balanced (activatable)" : `✗ blocked: ${bogoOut.blocks.join(", ")}`}</p>
            </div>
          )}
        </div>
      </div>

      {/* Create + list */}
      <div className="mt-6 border border-black/10 p-4">
        <h3 className="text-sm font-black uppercase tracking-wider mb-3">Promotions</h3>
        <div className="flex flex-wrap gap-2 items-end mb-4">
          <label className="block"><span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Type</span>
            <select data-testid="promo-type" value={ptype} onChange={(e) => setPtype(e.target.value)} className="mt-1 block border border-black/15 px-2 py-1.5 text-sm">
              <option value="taste_it">Taste It</option><option value="bogo">BOGO Express</option>
            </select></label>
          <Field label="Name" data-testid="promo-name" value={name} onChange={(e) => setName(e.target.value)} />
          <button onClick={create} data-testid="promo-create-btn" className="bg-black text-white font-bold px-4 py-2 text-sm hover:opacity-90">Create Draft</button>
        </div>
        <div data-testid="promo-list" className="text-sm">
          {promos.length === 0 ? <p className="text-muted-foreground">No promotions yet.</p> :
            promos.map((p) => <div key={p.id} className="flex justify-between border-b border-black/5 py-1.5"><span>{p.name} <span className="text-xs text-muted-foreground">({p.type})</span></span><span className="text-xs font-bold uppercase">{p.status}</span></div>)}
        </div>
      </div>
    </div>
  );
}
